import { store } from "store";
//#region types
import { DateHelper } from "utils/helpers";
import { AuthEnums } from "models/enums";
import { WintUtilsWebApi, WintSuperkollAPIModelTimeReporting, WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { BusinessUnitFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
import useAPIClientHelper from "./useAPIClientHelper";
import { IconDefinitionConstants, ServiceConstants } from "utils/constants";
import { CompanyService } from "services";
import { ThunkDispatch } from "redux-thunk";
import { loadInvoiceDimensions } from "store/actions/commonActions";
import businessUnitService from "services/businessUnitService";

function useBusinessUnitService() {
  const { apiClientInstance } = useAPIClientHelper();

  const isTimeReportingEnabled = () => {
    return CompanyService.isTimeReportingEnabled();
  };

  const hasCreatePermissions = () => {
    const isSalaryAdmin = CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.SalaryAdmin);
    return isBusinessUnitsAvailable() && isSalaryAdmin;
  };

  const hasEditPermissions = () => {
    return hasCreatePermissions() || isBusinessUnitManager();
  };

  const hasRestrictedEditPermissions = () => {
    const isSalaryAdmin = CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.SalaryAdmin);
    return !isSalaryAdmin && isBusinessUnitManager();
  };

  /**
   * @deprecated This function is deprecated. Move to a custom hook and use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Create a new custom hook (e.g., useBusinessUnitPermissions)
   * 2. Use useSelector to access Redux state instead of store.getState()
   * 3. Move this logic into the custom hook
   * 4. Use the hook in components instead of calling this function
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const isManager = businessUnitService.isBusinessUnitManager();
   *
   * @example
   * // New way (recommended):
   * function useBusinessUnitPermissions() {
   *   const loggedInUserIsBusinessUnitManager = useSelector((state: AppState) => state.auth.selectedCompany.loggedInUserIsBusinessUnitManager);
   *
   *   const isBusinessUnitManager = () => {
   *     return loggedInUserIsBusinessUnitManager;
   *   };
   *
   *   return { isBusinessUnitManager };
   * }
   *
   * // In component:
   * const { isBusinessUnitManager } = useBusinessUnitPermissions();
   * const isManager = isBusinessUnitManager();
   */
  const isBusinessUnitManager = () => {
    const state = store.getState();
    return state.auth.selectedCompany.loggedInUserIsBusinessUnitManager;
  };

  const hasReadPermissions = () => {
    return hasCreatePermissions() || isBusinessUnitManager();
  };

  const searchBusinessManagers = (searchStr: string, excludeSelf?: boolean) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .BusinessUnitsClient.Managers({
        ...ServiceConstants.DefaultFilter,
        searchStr: searchStr,
        excludeSelf: excludeSelf ?? true,
      })
      .then((res) => (res.data.items ? res.data.items : []));
  };

  const searchBusinessMangersAutoComplete = (searchStr: string, excludeSelf?: boolean) => {
    return searchBusinessManagers(searchStr, excludeSelf).then((result) =>
      result.map(
        (businessUnitManager): AutocompleteListItem => ({
          id: businessUnitManager.id,
          value: businessUnitManager.name ?? "",
          icon: IconDefinitionConstants.WUser,
        })
      )
    );
  };

  const businessUnitsReport = async (pagination: WintUtilsWebApi.Filter, filter: BusinessUnitFormTypes.Filter) => {
    let fromDate: Date | undefined;
    let toDate: Date | undefined;
    if (filter?.period !== undefined) {
      fromDate = DateHelper.resetTime(DateHelper.getFirstDayOfMonth(filter?.period));
      toDate = DateHelper.resetTime(DateHelper.getLastDayOfMonth(filter?.period));
    }

    return apiClientInstance(AuthEnums.AuthType.Company).BusinessUnitsClient.Report({
      ...pagination,
      ...filter,
      from: fromDate,
      to: toDate,
    });
  };

  const getBusinessUnit = async (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .BusinessUnitsClient.GetById(id)
      .then((res) => res.data);
  };

  const deleteBusinessUnit = async (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .BusinessUnitsClient.Delete(id)
      .then((res) => res.data);
  };

  const createBusinessUnit = (
    businessUnit: WintSuperkollAPIModelTimeReporting.BusinessUnitPost,
    dispatch: ThunkDispatch<any, any, any>
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .BusinessUnitsClient.Post(businessUnit)
      .then(async (res) => {
        dispatch(loadInvoiceDimensions());
        return res;
      });
  };

  const updateBusinessUnit = (
    id: string,
    businessUnit: WintSuperkollAPIModelTimeReporting.BusinessUnit,
    dispatch: ThunkDispatch<any, any, any>
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .BusinessUnitsClient.Put(id, businessUnit)
      .then(async (res) => {
        dispatch(loadInvoiceDimensions());
        return res;
      });
  };

  const availablePersons = (searchStr: string, excludeIds?: number[]) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .BusinessUnitsClient.AvailablePersons({
        ...ServiceConstants.DefaultFilter,
        searchStr: searchStr,
        excludeSelf: false,
      })
      .then((res) =>
        res.data.items ? res.data.items.filter((p) => p.id && !excludeIds?.includes(parseInt(p.id))) : []
      );
  };

  const availablePersonsAutoComplete = (searchStr: string, excludeIds?: number[]) => {
    return availablePersons(searchStr, excludeIds).then((result) =>
      result
        .map(
          (employee): AutocompleteListItem => ({
            id: employee.id,
            value: employee.name ?? "",
            icon: IconDefinitionConstants.WUser,
          })
        )
        .sort((a, b) => a.value.localeCompare(b.value))
    );
  };

  const searchBusinessUnitsAutoComplete = async (searchStr?: string) => {
    return businessUnitService.searchBusinessUnits(searchStr).then((res) =>
      res.map(
        (businessUnit): AutocompleteListItem => ({
          id: businessUnit.id,
          value: businessUnit.name ?? "",
          icon: IconDefinitionConstants.WCustomer,
        })
      )
    );
  };

  /**
   * @deprecated This function is deprecated. Move to a custom hook and use useSelector for Redux state access.
   *
   * Migration guide:
   * 1. Create a new custom hook (e.g., useBusinessUnitPermissions)
   * 2. Use useSelector to access Redux state instead of store.getState()
   * 3. Move this logic into the custom hook
   * 4. Use the hook in components instead of calling this function
   *
   * @see useSelector - React Redux hook for accessing state
   * @see https://app.clickup.com/4523827/v/dc/4a1tk-21632/4a1tk-8592
   *
   * @example
   * // Old way (deprecated):
   * const isEnabled = businessUnitService.isBusinessUnitsEnabled();
   *
   * @example
   * // New way (recommended):
   * function useBusinessUnitPermissions() {
   *   const company = useSelector((state: AppState) => state.auth.selectedCompany);
   *
   *   const isBusinessUnitsEnabled = () => {
   *     return (
   *       company.isBusinessUnitsEnabled &&
   *       company.superkollSalaryType === WintSuperkollAPIModel.SuperkollSalaryType.Deviations
   *     );
   *   };
   *
   *   return { isBusinessUnitsEnabled };
   * }
   *
   * // In component:
   * const { isBusinessUnitsEnabled } = useBusinessUnitPermissions();
   * const isEnabled = isBusinessUnitsEnabled();
   */
  const isBusinessUnitsEnabled = () => {
    const state = store.getState();
    return (
      state.auth.selectedCompany.isBusinessUnitsEnabled &&
      state.auth.selectedCompany.superkollSalaryType === WintSuperkollAPIModel.SuperkollSalaryType.Deviations
    );
  };

  const isBusinessUnitsAvailable = () => {
    return isTimeReportingEnabled() || isBusinessUnitsEnabled();
  };
  return {
    isTimeReportingEnabled,
    hasCreatePermissions,
    hasEditPermissions,
    hasRestrictedEditPermissions,
    isBusinessUnitManager,
    hasReadPermissions,
    searchBusinessManagers,
    searchBusinessMangersAutoComplete,
    businessUnitsReport,
    getBusinessUnit,
    deleteBusinessUnit,
    createBusinessUnit,
    updateBusinessUnit,
    availablePersons,
    availablePersonsAutoComplete,
    searchBusinessUnitsAutoComplete,
    isBusinessUnitsEnabled,
    isBusinessUnitsAvailable,
  };
}

export default useBusinessUnitService;
